import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * A nook's menu: Pin or Unpin and Archive or Restore, and on a draft Rename and Let go first. It
 * sits outside any link, so a press here never opens the nook.
 */
export function PinMenu({
  pinned,
  onToggle,
  onArchive,
  onRename,
  onLetGo,
  archived = false,
  horizontal = false,
  className = '',
}: {
  pinned: boolean;
  /** Renames the nook where its name is written; offered on a draft's page. */
  onRename?: () => void;
  /** Lets a draft go; offered on a draft's page, inside the menu and never beside it. */
  onLetGo?: () => void;
  /** The three dots lie in a row, as on a page's head. */
  horizontal?: boolean;
  /** Pin or unpin; absent for an archived nook, which is not pinned. */
  onToggle?: () => void;
  /** Archive the nook, or restore an archived one; absent where it cannot be done. */
  onArchive?: () => void;
  archived?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<CSSProperties>({ visibility: 'hidden' });

  // Under the ⋮, right edges aligned, kept inside the window.
  useLayoutEffect(() => {
    if (!open || !button.current || !sheet.current) return;
    const place = () => {
      const a = button.current!.getBoundingClientRect();
      const w = sheet.current!.offsetWidth;
      const left = Math.max(8, Math.min(a.right - w, window.innerWidth - w - 8));
      setAt({ top: a.bottom + 6, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <span ref={root} className={`pin-menu ${className}`.trim()}>
      <button
        ref={button}
        type="button"
        className="pin-menu-btn"
        aria-label="More"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <KebabGlyph horizontal={horizontal} />
      </button>
      {open && (
        <div ref={sheet} className="pin-menu-sheet" role="menu" style={at}>
          {onRename && (
            <button
              type="button"
              role="menuitem"
              autoFocus
              onClick={() => {
                setOpen(false);
                onRename();
              }}
            >
              Rename
            </button>
          )}
          {onLetGo && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onLetGo();
              }}
            >
              Let go
            </button>
          )}
          {onToggle && (
            <button
              type="button"
              role="menuitem"
              autoFocus={!onRename}
              onClick={() => {
                setOpen(false);
                onToggle();
              }}
            >
              <PinGlyph filled={pinned} />
              {pinned ? 'Unpin' : 'Pin'}
            </button>
          )}
          {onArchive && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onArchive();
              }}
            >
              <ArchiveGlyph />
              {archived ? 'Restore' : 'Archive'}
            </button>
          )}
        </div>
      )}
    </span>
  );
}

export function PinGlyph({ filled = false, size = 16 }: { filled?: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8.5 3.5h7M10 3.5v6L6.8 13v1.8h10.4V13L14 9.5v-6" fill={filled ? 'currentColor' : 'none'} />
      <path d="M12 14.8V21" />
    </svg>
  );
}

function ArchiveGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="4.5" width="17" height="4.5" rx="1.5" />
      <path d="M5.5 9v8.5a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V9M10 13h4" />
    </svg>
  );
}

function KebabGlyph({ horizontal }: { horizontal: boolean }) {
  const at = horizontal ? [[5, 12], [12, 12], [19, 12]] : [[12, 5], [12, 12], [12, 19]];
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {at.map(([cx, cy]) => (
        <circle key={`${cx},${cy}`} cx={cx} cy={cy} r="1.7" />
      ))}
    </svg>
  );
}
